/*
 * Route geometry for the live trip map.
 *
 * Everything works in a local metre grid (equirectangular around the point of
 * interest): the distances involved are a few km, where that is accurate to
 * well under a metre and far cheaper than haversine per vertex per ping.
 */

const R = 6371000;
const rad = (d) => (d * Math.PI) / 180;

/** [lng, lat] -> metres east/north of `origin`. */
const toLocal = (c, origin) => ({
  x: rad(c[0] - origin[0]) * R * Math.cos(rad(origin[1])),
  y: rad(c[1] - origin[1]) * R,
});

export const haversineM = ([lng1, lat1], [lng2, lat2]) => {
  const dLat = rad(lat2 - lat1);
  const dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

/** Compass bearing 0-360 from `a` to `b`, 0 = north, 90 = east. */
export const bearing = (a, b) => {
  const { x, y } = toLocal(b, a);
  if (!x && !y) return null;
  return (Math.atan2(x, y) * 180 / Math.PI + 360) % 360;
};

/** Total length of a [lng, lat] polyline in metres. */
export const lengthM = (points) => {
  let m = 0;
  for (let i = 1; i < points.length; i += 1) m += haversineM(points[i - 1], points[i]);
  return m;
};

/**
 * Where on the route a position is.
 *
 * Returns the closest point on the polyline, which segment it sits on, how
 * far along that segment (0-1), and how far the raw position is from the road.
 * That last number is the re-route trigger: a rider who is 70 m from every
 * segment has left the route we drew.
 */
export const snapToRoute = (points, c) => {
  if (!points || points.length < 2 || !c) return null;
  const p = toLocal(c, c);           // origin at c, so p is (0, 0)
  let best = null;
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = toLocal(points[i], c);
    const b = toLocal(points[i + 1], c);
    const dx = b.x - a.x; const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
    const qx = a.x + dx * t; const qy = a.y + dy * t;
    const d = Math.hypot(p.x - qx, p.y - qy);
    if (!best || d < best.distM) {
      best = {
        index: i,
        t,
        distM: d,
        point: [
          points[i][0] + (points[i + 1][0] - points[i][0]) * t,
          points[i][1] + (points[i + 1][1] - points[i][1]) * t,
        ],
      };
    }
  }
  return best;
};

/** The part of the route still ahead of a snapped position. */
export const remainingRoute = (points, snap) => {
  if (!snap) return points || [];
  return [snap.point, ...points.slice(snap.index + 1)];
};

/**
 * The road vertices between two snapped positions, for running the bike along
 * the bends rather than cutting across them. Null when the rider went
 * backwards, which is not something worth animating.
 */
export const routeBetween = (points, from, to) => {
  if (!from || !to) return null;
  if (to.index < from.index || (to.index === from.index && to.t < from.t)) return null;
  const mid = points.slice(from.index + 1, to.index + 1);
  return [from.point, ...mid, to.point];
};

/** Metres of the route ahead of a snapped position. */
export const remainingM = (points, snap) => lengthM(remainingRoute(points, snap));
